import React, { FC, useMemo } from "react";
import classNames from "classnames";
import { FinancialDocumentsUpload } from "../../../documents/FinancialDocumentUpload/FinancialDocumentsUpload";
import { Button } from "../../../general/Button/Button";
import { UploadIcon, CheckIcon, AlertCircleIcon } from "../../../../icons";
import { Loader } from "../../../general/Loader";
import {
    GridTable,
    GridTableRow,
} from "../../../general/Tables/GridTable/GridTable";
import { UploadingDocumentPlaceholder } from "../../../documents/lib";
import styles from "./Uploader.module.scss";

interface UploadReceiptsProps {
    onViewAll: () => void;
    items: UploadingDocumentPlaceholder[];
}

function getStatus(
    item: UploadingDocumentPlaceholder,
): "MATCHED" | "NOT_MATCHED" | "PROCESSING" | "ERROR" | null {
    const doc = item.financialDocument;
    if (!doc) {
        return null;
    }
    if (item.isUploading || !doc.hasAttemptedMatching) {
        return "PROCESSING";
    }
    if (!doc.ocrSuccessful) {
        return "ERROR";
    }
    if (doc.transactionMatches?.length) {
        return "MATCHED";
    }
    return "NOT_MATCHED";
}

export const UploadReceipts: FC<UploadReceiptsProps> = ({
    items,
    onViewAll,
}) => {
    const groupedCounts = useMemo(() => {
        let matched = 0,
            notMatched = 0,
            processing = 0,
            errors = 0;
        for (const item of items) {
            const status = getStatus(item);
            if (status === "MATCHED") {
                matched++;
            } else if (status === "NOT_MATCHED") {
                notMatched++;
            } else if (status === "PROCESSING") {
                processing++;
            } else if (status === "ERROR") {
                errors++;
            }
        }
        return { matched, notMatched, processing, errors };
    }, [items]);

    return (
        <GridTable className={styles.receiptsTable}>
            {groupedCounts.matched > 0 && (
                <SummaryRow
                    count={groupedCounts.matched}
                    label="Matched receipts"
                    icon={<CheckIcon className="icon-color-blue-600" />}
                    selected
                />
            )}
            {groupedCounts.notMatched > 0 && (
                <SummaryRow
                    count={groupedCounts.notMatched}
                    label="Receipts not matched"
                    icon={<span className="text-gray-500">•</span>}
                />
            )}
            {groupedCounts.processing > 0 && (
                <SummaryRow
                    count={groupedCounts.processing}
                    label="Processing receipts"
                    icon={<Loader inline />}
                />
            )}
            {groupedCounts.errors > 0 && (
                <SummaryRow
                    count={groupedCounts.errors}
                    label="Errors"
                    icon={<AlertCircleIcon className="icon-color-yellow-600" />}
                />
            )}
            <footer className={classNames(styles.receiptsFooter)}>
                <Button variant="tertiary" onClick={onViewAll} size="lg">
                    View All
                </Button>
                <FinancialDocumentsUpload
                    render={(open) => (
                        <Button variant="tertiary" onClick={open} size="lg">
                            <UploadIcon />
                            Upload another receipt
                        </Button>
                    )}
                />
            </footer>
        </GridTable>
    );
};

interface SummaryRowProps {
    count: number;
    label: string;
    icon: React.ReactNode;
    selected?: boolean;
}

const SummaryRow: FC<SummaryRowProps> = ({ count, label, icon, selected }) => (
    <GridTableRow
        className={classNames(styles.summaryRow, {
            [styles.selected]: selected,
        })}
    >
        <div className={styles.count}>{count}</div>
        <div className={styles.label}>{label}</div>
        <div className={styles.icon}>{icon}</div>
    </GridTableRow>
);
